import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  Info,
  Users,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { noPunchLabel } from './PunchInsTable';

export interface StaffAttendanceStatsProps {
  activeTab: 'punchins' | 'report' | 'leaves';
  /** Active staff. Null when the staff list could not be read — unknown, not zero. */
  totalStaff: number | null;
  summary: {
    present: number;
    /** Punches nobody in the office has checked yet — not a measure of lateness. */
    awaiting: number;
    /** No punch and not on leave. Null with the staff list, for the same reason. */
    notIn: number | null;
    onLeave: number;
  };
  /** Whether the day on screen is today — what a row with no punch is called. */
  isToday: boolean;
  leaveSummary: {
    total: number;
    approved: number;
    pending: number;
    rejected: number;
    cancelled: number;
  };
  /**
   * The row status the table is filtered to, so its card reads as pressed.
   * Null when no card stands for the table on screen.
   */
  activeStatus: string | null;
  /** A card narrows the table to its rows; '' is the total, showing everyone. */
  onSelect: (status: string) => void;
}

export function StaffAttendanceStats({
  activeTab,
  totalStaff,
  summary,
  isToday,
  leaveSummary,
  activeStatus,
  onSelect,
}: StaffAttendanceStatsProps) {
  const isLeaves = activeTab === 'leaves';

  const formatPct = (count: number, total: number) => {
    if (!total || total <= 0) return '0.00%';
    const pct = Math.min(100, Math.max(0, (count / total) * 100));
    return `${pct.toFixed(2)}%`;
  };

  if (isLeaves) {
    const total = leaveSummary.total || 1;
    const cards = [
      {
        label: 'Total requests',
        status: '',
        sublabel: 'All leave requests',
        value: leaveSummary.total,
        pct: '100%',
        cardStyle: 'bg-[#eff6ff] dark:bg-slate-900 border-[#bfdbfe] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
        dotStyle: 'bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white shadow-md shadow-blue-500/35 dark:shadow-none',
        labelStyle: 'text-blue-950 dark:text-white',
        barColor: 'bg-blue-600',
        icon: <CalendarDays className="h-4 w-4" />,
      },
      {
        label: 'Approved',
        status: 'APPROVED',
        sublabel: `${formatPct(leaveSummary.approved, total)} of total`,
        value: leaveSummary.approved,
        pct: formatPct(leaveSummary.approved, total),
        cardStyle: 'bg-[#eaf8f0] dark:bg-slate-900 border-[#c3eed4] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
        dotStyle: 'bg-gradient-to-br from-[#10b981] to-[#059669] text-white shadow-md shadow-emerald-500/35 dark:shadow-none',
        labelStyle: 'text-emerald-950 dark:text-white',
        barColor: 'bg-emerald-500',
        icon: <CheckCircle2 className="h-4 w-4" />,
      },
      {
        label: 'Pending',
        status: 'PENDING',
        sublabel: `${formatPct(leaveSummary.pending, total)} of total`,
        value: leaveSummary.pending,
        pct: formatPct(leaveSummary.pending, total),
        cardStyle: 'bg-[#fffbeb] dark:bg-slate-900 border-[#fde68a] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
        dotStyle: 'bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-white shadow-md shadow-amber-500/35 dark:shadow-none',
        labelStyle: 'text-amber-950 dark:text-white',
        barColor: 'bg-amber-500',
        icon: <Clock3 className="h-4 w-4" />,
      },
      {
        label: 'Rejected',
        status: 'REJECTED',
        sublabel: `${formatPct(leaveSummary.rejected, total)} of total`,
        value: leaveSummary.rejected,
        pct: formatPct(leaveSummary.rejected, total),
        cardStyle: 'bg-[#fff1f2] dark:bg-slate-900 border-[#fecdd3] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
        dotStyle: 'bg-gradient-to-br from-[#f43f5e] to-[#e11d48] text-white shadow-md shadow-rose-500/35 dark:shadow-none',
        labelStyle: 'text-rose-950 dark:text-white',
        barColor: 'bg-rose-500',
        icon: <XCircle className="h-4 w-4" />,
      },
      {
        label: 'Cancelled',
        status: 'CANCELLED',
        sublabel: `${formatPct(leaveSummary.cancelled, total)} of total`,
        value: leaveSummary.cancelled,
        pct: formatPct(leaveSummary.cancelled, total),
        cardStyle: 'bg-[#fbf0ff] dark:bg-slate-900 border-[#f0c8fe] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
        dotStyle: 'bg-gradient-to-br from-[#a855f7] to-[#9333ea] text-white shadow-md shadow-purple-500/35 dark:shadow-none',
        labelStyle: 'text-purple-950 dark:text-white',
        barColor: 'bg-purple-500',
        icon: <Info className="h-4 w-4" />,
      },
    ];

    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map((card, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onSelect(card.status)}
            aria-pressed={activeStatus === card.status}
            className={cn(
              'rounded-2xl border p-3.5 flex flex-col justify-between text-left cursor-pointer hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
              card.cardStyle,
              activeStatus === card.status && '!border-accent ring-1 ring-inset ring-accent/30',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <span className={cn('text-xs sm:text-[14px] font-bold block leading-tight truncate', card.labelStyle)}>
                  {card.label}
                </span>
                <span className="font-black text-slate-800 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
                  {card.value}
                </span>
              </div>
              <div className={cn('h-8 w-8 sm:h-9 sm:w-9 rounded-xl flex items-center justify-center shrink-0', card.dotStyle)}>
                {card.icon}
              </div>
            </div>

            <span className="text-[11px] sm:text-xs font-bold text-slate-500 dark:text-slate-400 block mt-1 truncate">
              {card.sublabel}
            </span>

            <div className="w-full bg-slate-200/70 dark:bg-slate-800 rounded-full h-1.5 mt-2 overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all duration-500', card.barColor)}
                style={{ width: card.pct }}
              />
            </div>
          </button>
        ))}
      </div>
    );
  }

  /** A share of the staff. Null while the staff total is unknown — no share can be given. */
  const shareOf = (count: number | null) =>
    count === null || totalStaff === null ? null : formatPct(count, totalStaff);
  const sublabelOf = (count: number | null) => `${shareOf(count) ?? '—'} of total`;
  const cards = [
    {
      label: 'Total staff',
      status: '',
      sublabel: 'Active staff accounts',
      value: totalStaff ?? '—',
      pct: totalStaff === null ? '0%' : '100%',
      cardStyle: 'bg-[#eff6ff] dark:bg-slate-900 border-[#bfdbfe] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
      dotStyle: 'bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white shadow-md shadow-blue-500/35 dark:shadow-none',
      labelStyle: 'text-blue-950 dark:text-white',
      barColor: 'bg-blue-600',
      icon: <Users className="h-4 w-4" />,
    },
    {
      label: 'Present',
      status: 'APPROVED',
      sublabel: sublabelOf(summary.present),
      value: summary.present,
      pct: shareOf(summary.present) ?? '0%',
      cardStyle: 'bg-[#eaf8f0] dark:bg-slate-900 border-[#c3eed4] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
      dotStyle: 'bg-gradient-to-br from-[#10b981] to-[#059669] text-white shadow-md shadow-emerald-500/35 dark:shadow-none',
      labelStyle: 'text-emerald-950 dark:text-white',
      barColor: 'bg-emerald-500',
      icon: <CheckCircle2 className="h-4 w-4" />,
    },
    {
      // What the office has still to check — a punch at any time of day can be
      // waiting, so this is not a count of late arrivals.
      label: 'Awaiting check',
      status: 'PENDING',
      sublabel: sublabelOf(summary.awaiting),
      value: summary.awaiting,
      pct: shareOf(summary.awaiting) ?? '0%',
      cardStyle: 'bg-[#fffbeb] dark:bg-slate-900 border-[#fde68a] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
      dotStyle: 'bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-white shadow-md shadow-amber-500/35 dark:shadow-none',
      labelStyle: 'text-amber-950 dark:text-white',
      barColor: 'bg-amber-500',
      icon: <Clock3 className="h-4 w-4" />,
    },
    {
      label: noPunchLabel(isToday),
      status: 'NOT_IN',
      sublabel: sublabelOf(summary.notIn),
      value: summary.notIn ?? '—',
      pct: shareOf(summary.notIn) ?? '0%',
      cardStyle: 'bg-[#fff1f2] dark:bg-slate-900 border-[#fecdd3] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
      dotStyle: 'bg-gradient-to-br from-[#f43f5e] to-[#e11d48] text-white shadow-md shadow-rose-500/35 dark:shadow-none',
      labelStyle: 'text-rose-950 dark:text-white',
      barColor: 'bg-rose-500',
      icon: <XCircle className="h-4 w-4" />,
    },
    // {
    //   label: 'On leave',
    //   sublabel: `${formatPct(summary.onLeave, effectiveTotal)} of total`,
    //   value: summary.onLeave,
    //   pct: formatPct(summary.onLeave, effectiveTotal),
    //   cardStyle: 'bg-[#fbf0ff] dark:bg-slate-900 border-[#f0c8fe] dark:border-slate-800 shadow-md hover:shadow-xl dark:shadow-none transition-all',
    //   dotStyle: 'bg-gradient-to-br from-[#a855f7] to-[#9333ea] text-white shadow-md shadow-purple-500/35 dark:shadow-none',
    //   labelStyle: 'text-purple-950 dark:text-white',
    //   barColor: 'bg-purple-500',
    //   icon: <CalendarDays className="h-4 w-4" />,
    // },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4 ">
      {cards.map((card, idx) => (
        <button
          key={idx}
          type="button"
          onClick={() => onSelect(card.status)}
          aria-pressed={activeStatus === card.status}
          className={cn(
            'rounded-2xl border p-3.5 flex flex-col justify-between text-left cursor-pointer hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
            card.cardStyle,
            activeStatus === card.status && '!border-accent ring-1 ring-inset ring-accent/30',
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <span className={cn('text-xs sm:text-[14px] font-bold block leading-tight truncate', card.labelStyle)}>
                {card.label}
              </span>
              <span className="font-black text-slate-800 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
                {card.value}
              </span>
            </div>
            <div className={cn('h-8 w-8 sm:h-9 sm:w-9 rounded-xl flex items-center justify-center shrink-0', card.dotStyle)}>
              {card.icon}
            </div>
          </div>

          <span className="text-[11px] sm:text-xs font-bold text-slate-500 dark:text-slate-400 block mt-1 truncate">
            {card.sublabel}
          </span>

          <div className="w-full bg-slate-200/70 dark:bg-slate-800 rounded-full h-1.5 mt-2 overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-500', card.barColor)}
              style={{ width: card.pct }}
            />
          </div>
        </button>
      ))}
    </div>
  );
}
